import * as _ from 'lodash';
import qs from 'qs';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { InjectedRouter } from 'react-router';
import type { SetOptional } from 'type-fest';

import { parseQueryParams } from '../../../../helpers/issuesV2';
import { useDebouncedCallback } from '../../../../hooks';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { HydrateStateQuery } from '../../../../redux/issuesV2/types';
import type { IssuesContainerProps } from '../../IssuesContainer';

import useFetchIssuesAction from './useFetchIssuesAction';

type UseSyncStateAndURLParamsProps = IssuesContainerProps & {
  router: InjectedRouter;
};

/**
 * Takes the location.search and parses it out, so it can be
 * synced with the Redux state.
 *
 * @param {string} search - The router's location.search
 */
const parseSearchParams = (search: string) => {
  const parsed = qs.parse(search, {
    //  `search` usually starts with a "?", so this allows us to ignore that.
    ignoreQueryPrefix: true,
  });
  return parseQueryParams(parsed);
};

/**
 * Helper for omitting the issueCategory, which is not needed in the URL,
 * as it is part of the location.pathanme param.
 *
 * @param {HydrateStateQuery} query - The hydrate state query.
 */
const omitCategory = (query: HydrateStateQuery) => _.omit(query, ['issueCategory']);

/**
 * If search is undefined/empty, we don't want it to be added to the URL params.
 *
 * @param {HydrateStateQuery} query - The hydrate state query (minus the issueCategory).
 */
const omitUndefinedSearch = (query: SetOptional<HydrateStateQuery, 'issueCategory'>) => !query.search
  ? _.omit(query, ['search'])
  : query;

/**
 * Takes the query, omits things which don't go in the query params, stringifies them.
 *
 * @param {HydrateStateQuery} query - The hydrate state query.
 */
const stringifyQuery = (query: HydrateStateQuery): string => qs.stringify(
  omitUndefinedSearch(
    omitCategory(query),
  ),
);

/**
 * Hook which keeps the Redux state and the URL params in sync.
 * Does this by running one of two effects depending on if the
 * Redux state was updated or if the URL/router location has been updated.
 *
 * @param syncState - syncState Object
 * @param {InjectedRouter} syncState.router - React router.
 * @param {RouterProps["location"]} syncState.location - The history location object.
 * @param {Record<string, unknown>} syncState.params - The URL params (note these are not the same as the location.search query).
 */
const useSyncStateAndURLParams = ({ router, location, params }: UseSyncStateAndURLParamsProps): void => {
  //  This is the bits of the state which can be stored in the URL.
  const hydrateStateQuery = useSelector(IssuesV2Store.selectors.getHydrateStateQuery);
  //  This action is bound with fetchIssues, meaning it will call the debounced fetchIssues query after being dispatched.
  const hydrateState = useFetchIssuesAction(IssuesV2Store.actions.hydrateState);

  //  Updates the URL params when the state changes.
  //  Debounced because we don't want to thrash the URL updates
  const syncURLParams = useDebouncedCallback((pathname: string, query: HydrateStateQuery) =>
    router.replace({
      pathname,
      search: `?${stringifyQuery(query)}`,
    }));

  /**
   * When the component mounts OR the user switches categories, we dispatch a hydrateState action.
   * This will sync the query params into the state (if they exist,
   * the reducer handles bad/empty values for us).
   *
   * Note that dispatching hydrateState also kicks off the initial
   * fetchIssues request (see: `useFetchIssuesAction`).
   */
  useEffect(() => {
    const parsedSearchParams = parseSearchParams(location?.search ?? '');
    hydrateState({
      ...parsedSearchParams,
      //  We also want changes to the category to be reflected in the state, so we include this.
      issueCategory: params.category,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.category]);

  /**
   * Effect which runs only when the state changes,
   * This is responsible for updating the URL params based on the updated Redux state.
   * Note that we are only looking at a subset of the state which would
   * actually go into the query params (see `HydrateStateQuery`).
   */
  useEffect(() => {
    syncURLParams(location.pathname, hydrateStateQuery);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- We are intentionally ignoring location, as we want this to run only when the state is updated.
  }, [hydrateStateQuery]);
};

export default useSyncStateAndURLParams;
